import type { CSSProperties, ReactNode } from 'react';
import { cn } from '../utils/cn';

// =============================================================================
// PageContainer (W7) — single source of truth for the admin <main> body layout.
//
// Before W7 each section page rolled its own root container, so width, padding
// and alignment drifted (p-6 vs p-8, mx-auto centering on BO/Org, full-bleed
// editors). The BoPage `mx-auto max-w-5xl` in particular made the BO surface
// jump to center while every other section stayed left-aligned.
//
// W7 unifies on LEFT alignment everywhere with one consistent gutter so content
// lines up with the section sub-tabs / header left edge. Two width modes:
//
//   variant="full"    — editors/canvases that own the whole pane (Playlists,
//                        Schedules, Devices, DeviceDetail). Just `h-full` and a
//                        consistent outer gutter; inner scroll stays as-is.
//   variant="content" — forms/tables that read better capped (Organization,
//                       AuditLog, Notifications, all BO pages). Scrollable body
//                       with a left-aligned max-width column — `max-w-5xl`, NO
//                       `mx-auto`, so it starts at the same left gutter.
//
// Container-only: this changes width/alignment/padding, never page internals.
//
// 2026-09-14 (C-1 3차): 메뉴마다 바깥 여백이 16~24px, 제목 상단이 22~29px로 흔들려
// 한 규칙으로 고정한다. 모든 메뉴 바깥 여백은 24px(PAGE_GUTTER). 섹션 머리(제목+탭)
// 아래에 그려지는 화면은 위쪽만 16px(SECTION_BODY_GUTTER) — 제목의 mb-4와 같은 간격.
// =============================================================================

/** 모든 메뉴 화면의 바깥 여백. 제목·본문 좌측 24px, 상단 24px. */
export const PAGE_GUTTER = 'px-4 pt-4 pb-6 md:px-6 md:pt-6';
/** 섹션 머리(제목+탭)가 App.tsx에 있는 화면의 본문 여백. 탭 아래 16px. */
export const SECTION_BODY_GUTTER = 'px-4 pt-3 pb-6 md:px-6 md:pt-4';

export type PageContainerVariant = 'full' | 'content';

export function PageContainer({
  variant = 'content',
  column = true,
  fluid = false,
  inSection = false,
  className,
  style,
  children,
}: {
  variant?: PageContainerVariant;
  /** content-mode only. 섹션 머리(제목+탭) 아래 본문이면 true — 위 여백을 16px로. */
  inSection?: boolean;
  /** content-mode only. true면 max-w-5xl 캡 없이 우측 여백까지 꽉 채운다. */
  fluid?: boolean;
  /**
   * content-mode only. When true (default) children are wrapped in a vertical
   * `gap-5` flex column. Pages that manage their own block spacing (e.g. `mb-*`
   * margins) pass `column={false}` to get just the left-aligned `max-w-5xl`
   * cap without the extra gap — keeps their internals untouched.
   */
  column?: boolean;
  className?: string;
  /** Escape hatch for pages still styled with inline CSS (e.g. Devices). */
  style?: CSSProperties;
  children: ReactNode;
}): JSX.Element {
  if (variant === 'full') {
    // Full-bleed editors/canvases: they manage their own internal scroll and
    // padding, so we only guarantee the height context here. Any outer gutter
    // is opt-in via `className` to avoid double padding.
    return (
      <div className={cn('h-full min-h-0', className)} style={style}>
        {children}
      </div>
    );
  }

  // Content pages: consistent scroll + padding, left-aligned capped column.
  return (
    // relative — 안쪽 sr-only(absolute) 글자가 문서 끝까지 뻗어 빈 스크롤을 만들던 문제(2026-10-06, 고객사 상세). 스크롤 루트가 기준이 된다.
    <div className={cn('relative h-full overflow-auto', inSection ? SECTION_BODY_GUTTER : PAGE_GUTTER, className)} style={style}>
      {/* 블록 간격 16px — 제목(mb-0)→본문 간격이 PageTitle의 mb-4(16px)와 같아지도록 gap-5→gap-4. */}
      <div className={cn(fluid ? 'max-w-none' : 'max-w-5xl', column && 'flex flex-col gap-4')}>{children}</div>
    </div>
  );
}
